import React, { useState, useEffect } from 'react';
import { useAutosave } from '../../../hooks/useAutosave';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Save, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReasonWhyLetterForm = ({ caseId, agent, onUpdate }) => {
  const [data, setData] = useState({ content: '' });
  const [saving, setSaving] = useState(false);
  useAutosave(() => save(), data);
  const [generating, setGenerating] = useState(false);
  // Iter 40: manual reason override — bypasses fact-find/needs/quotes
  // completeness check and seeds the AI with the advisor's free text.
  const [manualReason, setManualReason] = useState('');
  const [showOverride, setShowOverride] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/compliance/cases/${caseId}/reason-why-letter`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d || { content: '' }))
      .catch(() => {});
  }, [caseId]);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/compliance/cases/${caseId}/reason-why-letter?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: data.content, finalized: data.finalized })
      });
      onUpdate && onUpdate();
    } finally { setSaving(false); }
  };

  const generateAI = async () => {
    setGenerating(true);
    try {
      const r = await fetch(`${API_URL}/api/compliance/cases/${caseId}/ai-reason-why-letter?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ manual_reason: manualReason.trim() || undefined }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'AI generation failed');
      }
      const { content } = await r.json();
      setData(prev => ({ ...prev, content, ai_generated: true }));
      alert(manualReason.trim()
        ? 'Reason Why Letter expanded from your manual summary by AI. Review and Save when ready.'
        : 'Reason Why Letter drafted by AI. Review the content and click Save when ready.');
    } catch (e) {
      alert(e.message);
    } finally { setGenerating(false); }
  };

  return (
    <Card data-testid="reason-why-letter-card">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle>Reason Why Letter</CardTitle>
        <div className="flex gap-2">
          <Button onClick={generateAI} disabled={generating} variant="outline" className="border-[#D4AF37] text-[#003D5C]" data-testid="ai-reason-why-btn">
            <Sparkles className="h-4 w-4 mr-2" />{generating ? 'Generating...' : 'Generate with AI'}
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#0077B6]" data-testid="save-reason-why-btn">
            <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600">
          A detailed CLHIA-style Reason Why Letter summarizing the fact-findings (step 5), needs analysis (step 6),
          AML/KYC confirmation (step 7), and quotes presented (step 8). Sent to the client post-application.
          Click <b>Generate with AI</b> to draft the letter — review and edit before saving.
        </p>

        {/* Iter 40: Manual Reason Override */}
        <div className="border border-amber-200 bg-amber-50/40 rounded-lg">
          <button
            type="button"
            onClick={() => setShowOverride(v => !v)}
            className="w-full flex items-center justify-between px-3 py-2 text-left text-sm font-semibold text-amber-900 hover:bg-amber-100/40 rounded-lg"
            data-testid="rwl-override-toggle"
          >
            <span className="flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-amber-700" />
              Manual Reason Override (use when Application Stage sections are not filled in)
            </span>
            {showOverride ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {showOverride && (
            <div className="px-3 pb-3 space-y-2">
              <p className="text-xs text-amber-800">
                If you didn't complete Fact Findings, Needs Analysis or Quotes, write a short
                summary below (a few sentences is enough — e.g. <em>"Client is a 35-year-old
                non-smoker, needs $500K Term 20 to protect mortgage; chose Manulife Family Term
                at $42/mo."</em>). The AI will EXPOUND on it to produce a full CLHIA-style letter.
              </p>
              <textarea
                rows="4"
                value={manualReason}
                onChange={(e) => setManualReason(e.target.value)}
                placeholder="Write your short reason / summary here — AI will expand it into a full letter…"
                className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 bg-white"
                data-testid="rwl-manual-reason"
              />
              <p className="text-[11px] text-amber-700">
                Leave blank to use the standard AI path (requires Fact Find + Needs Analysis + Quotes).
              </p>
            </div>
          )}
        </div>

        {data.ai_generated && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            Letter was drafted by AI{data.ai_generated_at ? ` on ${new Date(data.ai_generated_at).toLocaleString()}` : ''}. Please review carefully before sending to the client.
          </div>
        )}
        <textarea
          rows="18"
          value={data.content || ''}
          onChange={(e) => setData({ ...data, content: e.target.value })}
          placeholder="Click 'Generate with AI' to draft the Reason Why letter, or type your own..."
          className="w-full px-3 py-2 border rounded-lg font-serif text-sm leading-relaxed"
          data-testid="rwl-content"
        />
        <label className="flex items-center gap-2 pt-2 border-t">
          <input type="checkbox" checked={data.finalized || false} onChange={(e) => setData({ ...data, finalized: e.target.checked })} data-testid="rwl-finalized" />
          <span className="text-sm font-semibold">Mark as Finalized (ready to send to client)</span>
        </label>
      </CardContent>
    </Card>
  );
};
